import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface AdminAgentFamily {
  skill: string;
  description: string;
  allowed_keys: string[];
  defaults: Record<string, unknown>;
  instance_count: number;
  enabled_instance_count: number;
  total_daily_budget_usd: string;
}

export interface AdminAgentInstance {
  instance_id: string;
  skill: string;
  parameters: Record<string, unknown>;
  enabled: boolean;
  overrides: Record<string, unknown>;
  effective: Record<string, unknown>;
  has_overrides: boolean;
  last_run_at: string | null;
  can_disable: boolean;
}

export interface AdminAgentsList {
  families: AdminAgentFamily[];
  instances: AdminAgentInstance[];
}

export interface AdminAgentAuditEntry {
  id: string;
  timestamp: string;
  actor_id: string;
  actor_display_name: string | null;
  action: string;
  payload: Record<string, unknown>;
}

export interface UpdateFamilyBody {
  defaults: Record<string, unknown>;
}

export interface UpdateInstanceBody {
  enabled?: boolean;
  overrides?: Record<string, unknown>;
}

export const adminAgentsKey = ["admin", "agents"] as const;
export const adminAgentAuditKey = (instanceId: string) =>
  ["admin", "agents", "audit", instanceId] as const;

export function useAdminAgents() {
  return useQuery<AdminAgentsList>({
    queryKey: adminAgentsKey,
    queryFn: () => apiFetch<AdminAgentsList>("/admin/agents"),
    staleTime: 30_000,
  });
}

export function useAgentInstanceAudit(instanceId: string | undefined, limit = 20) {
  return useQuery<AdminAgentAuditEntry[]>({
    queryKey: instanceId
      ? adminAgentAuditKey(instanceId)
      : ["admin", "agents", "audit", "__none__"],
    queryFn: () =>
      apiFetch<AdminAgentAuditEntry[]>(
        `/admin/agents/instances/${encodeURIComponent(instanceId ?? "")}/audit?limit=${limit}`,
      ),
    enabled: Boolean(instanceId),
    staleTime: 15_000,
  });
}

/**
 * PATCH /admin/agents/families/{skill}. Optimistic: the family row in
 * the cached list is patched locally; the server response replaces it
 * on success, the snapshot is restored on error.
 */
export function useUpdateAgentFamily(skill: string) {
  const qc = useQueryClient();
  return useMutation<AdminAgentFamily, Error, UpdateFamilyBody>({
    mutationFn: (body) =>
      apiFetch<AdminAgentFamily>(
        `/admin/agents/families/${encodeURIComponent(skill)}`,
        { method: "PATCH", body },
      ),
    onSuccess: (updated) => {
      qc.setQueryData<AdminAgentsList>(adminAgentsKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          families: prev.families.map((f) => (f.skill === skill ? updated : f)),
        };
      });
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminAgentsKey });
    },
  });
}

export function useUpdateAgentInstance(instanceId: string) {
  const qc = useQueryClient();
  return useMutation<AdminAgentInstance, Error, UpdateInstanceBody>({
    mutationFn: (body) =>
      apiFetch<AdminAgentInstance>(
        `/admin/agents/instances/${encodeURIComponent(instanceId)}`,
        { method: "PATCH", body },
      ),
    onSuccess: (updated) => {
      qc.setQueryData<AdminAgentsList>(adminAgentsKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          instances: prev.instances.map((i) =>
            i.instance_id === instanceId ? updated : i,
          ),
        };
      });
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: adminAgentsKey });
      await qc.invalidateQueries({ queryKey: adminAgentAuditKey(instanceId) });
    },
  });
}
